import React, { useState, useEffect } from 'react';
import { X, User, Brain, Loader, Save, Sparkles, Award, Wrench, MessageCircle } from 'lucide-react';
import PromptDebugger from '../Debug/PromptDebugger';
import { buildApiUrl, apiConfig } from '../../config/api.config';

interface ExpertProfile {
  role: string;
  specialties: string[];
  experience_years: number;
  domain_knowledge: string[];
  approach: string;
  strengths: string[];
  certifications?: string[];
  preferred_tools?: string[];
  communication_style?: string;
  ai_generated?: boolean;
  ai_reasoning?: string;
  generated_at?: string;
}

interface ExpertProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'project' | 'goal';
  entityId: string;
  entityName: string;
  onSave: (expertProfile: ExpertProfile) => void;
  loading?: boolean;
}

const ExpertProfileModal: React.FC<ExpertProfileModalProps> = ({
  isOpen,
  onClose,
  type,
  entityId,
  entityName,
  onSave,
  loading = false
}) => {
  const [expertProfile, setExpertProfile] = useState<ExpertProfile | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [formData, setFormData] = useState<ExpertProfile>({
    role: '',
    specialties: [],
    experience_years: 5,
    domain_knowledge: [],
    approach: '',
    strengths: [],
    certifications: [],
    preferred_tools: [],
    communication_style: ''
  });

  useEffect(() => {
    if (isOpen && entityId) {
      loadExpertProfile();
    }
  }, [isOpen, entityId]);

  const loadExpertProfile = async () => {
    try {
      const token = localStorage.getItem('projectai_token');
      const endpoint = type === 'project'
        ? `/api/ai/projects/${entityId}/expert`
        : `/api/ai/goals/${entityId}/expert`;

      const response = await fetch(buildApiUrl(endpoint), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.expertProfile) {
          setExpertProfile(data.expertProfile);
          setFormData(data.expertProfile);
        }
      }
    } catch (error) {
      console.error('Failed to load expert profile:', error);
    }
  };

  const generateExpertRecommendation = async () => {
    setIsGenerating(true);
    try {
      const token = localStorage.getItem('projectai_token');

      const response = await fetch(apiConfig.endpoints.ai.recommendExpert, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type,
          entityId,
          context: {
            source: 'user_request',
            timestamp: new Date().toISOString()
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        setExpertProfile(data.expertProfile);
        setFormData(data.expertProfile);
        setDebugInfo(data.debug);
        setIsEditing(true);
      } else {
        console.error('Failed to generate expert recommendation');
      }
    } catch (error) {
      console.error('Expert generation failed:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('projectai_token');
      const endpoint = type === 'project'
        ? `/api/ai/projects/${entityId}/expert`
        : `/api/ai/goals/${entityId}/expert`;

      const response = await fetch(buildApiUrl(endpoint), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          expertProfile: formData
        })
      });

      if (response.ok) {
        const data = await response.json();
        setExpertProfile(data.expertProfile);
        setIsEditing(false);
        onSave(data.expertProfile);
      }
    } catch (error) {
      console.error('Failed to save expert profile:', error);
    }
  };

  const updateArrayField = (field: keyof ExpertProfile, value: string) => {
    const items = value.split(',').map(item => item.trim()).filter(item => item);
    setFormData(prev => ({
      ...prev,
      [field]: items
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />

        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <User className="w-6 h-6 text-indigo-600" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Experten-Profil
                </h2>
                <p className="text-sm text-gray-600">
                  {type === 'project' ? 'Projekt' : 'Ziel'}: "{entityName}"
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto flex-1">
            <div className="p-6">
              {!expertProfile && !isEditing ? (
                <div className="text-center py-12">
                  <User className="mx-auto w-16 h-16 text-indigo-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Kein Experten-Profil definiert
                  </h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    Lassen Sie die KI einen passenden Experten für dieses {type === 'project' ? 'Projekt' : 'Ziel'} empfehlen
                    oder erstellen Sie manuell ein Profil.
                  </p>
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={generateExpertRecommendation}
                      disabled={isGenerating}
                      className="btn-primary flex items-center gap-2"
                    >
                      {isGenerating ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : (
                        <Brain className="w-4 h-4" />
                      )}
                      KI-Empfehlung generieren
                    </button>
                    <button
                      onClick={() => setIsEditing(true)}
                      className="btn-secondary"
                    >
                      Manuell erstellen
                    </button>
                  </div>
                </div>
              ) : isEditing ? (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rolle/Position
                    </label>
                    <input
                      type="text"
                      value={formData.role}
                      onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="z.B. Senior Full-Stack Developer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Spezialisierungen (kommagetrennt)
                    </label>
                    <input
                      type="text"
                      value={formData.specialties.join(', ')}
                      onChange={(e) => updateArrayField('specialties', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="z.B. React, Node.js, PostgreSQL, Cloud Architecture"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Erfahrungsjahre
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="30"
                      value={formData.experience_years}
                      onChange={(e) => setFormData(prev => ({ ...prev, experience_years: parseInt(e.target.value) }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Domänen-Wissen (kommagetrennt)
                    </label>
                    <input
                      type="text"
                      value={formData.domain_knowledge.join(', ')}
                      onChange={(e) => updateArrayField('domain_knowledge', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="z.B. E-Learning Platforms, SaaS Development, Multi-Tenant Architecture"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Entwicklungsansatz
                    </label>
                    <textarea
                      value={formData.approach}
                      onChange={(e) => setFormData(prev => ({ ...prev, approach: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      rows={2}
                      placeholder="z.B. Agile development with test-driven approach and continuous deployment"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Stärken (kommagetrennt)
                    </label>
                    <input
                      type="text"
                      value={formData.strengths.join(', ')}
                      onChange={(e) => updateArrayField('strengths', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="z.B. Scalable architecture design, User experience optimization"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {expertProfile?.ai_generated && (
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-5 h-5 text-indigo-600" />
                        <h3 className="font-medium text-indigo-900">KI-generiertes Profil</h3>
                      </div>
                      <p className="text-sm text-indigo-700">{expertProfile.ai_reasoning}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="flex items-center gap-2 font-medium text-gray-900 mb-3">
                        <User className="w-4 h-4" />
                        Grundinformationen
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-gray-600">Rolle:</span>
                          <span className="ml-2 font-medium">{expertProfile?.role}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Erfahrung:</span>
                          <span className="ml-2 font-medium">{expertProfile?.experience_years} Jahre</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="flex items-center gap-2 font-medium text-gray-900 mb-3">
                        <Wrench className="w-4 h-4" />
                        Spezialisierungen
                      </h4>
                      <div className="flex flex-wrap gap-1">
                        {expertProfile?.specialties.map((specialty, index) => (
                          <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="flex items-center gap-2 font-medium text-gray-900 mb-3">
                      <Award className="w-4 h-4" />
                      Domänen-Wissen
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {expertProfile?.domain_knowledge.map((domain, index) => (
                        <span key={index} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                          {domain}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Entwicklungsansatz</h4>
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                      {expertProfile?.approach}
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Stärken</h4>
                    <div className="space-y-1">
                      {expertProfile?.strengths.map((strength, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                          <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
                          {strength}
                        </div>
                      ))}
                    </div>
                  </div>

                  {expertProfile?.communication_style && (
                    <div>
                      <h4 className="flex items-center gap-2 font-medium text-gray-900 mb-2">
                        <MessageCircle className="w-4 h-4" />
                        Kommunikationsstil
                      </h4>
                      <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                        {expertProfile.communication_style}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Debug Information */}
          {debugInfo && (
            <div className="px-6 pb-6">
              <PromptDebugger
                title="Expert Recommendation AI Prompt"
                prompt={debugInfo.prompt}
                response={debugInfo.rawResponse}
                metadata={debugInfo.metadata}
              />
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 min-h-[80px]">
            <div className="flex items-center gap-3">
              {expertProfile && !isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn-secondary"
                >
                  Bearbeiten
                </button>
              )}
              {expertProfile && !isEditing && (
                <button
                  onClick={generateExpertRecommendation}
                  disabled={isGenerating}
                  className="btn-secondary flex items-center gap-2"
                >
                  {isGenerating ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Brain className="w-4 h-4" />
                  )}
                  Neu generieren
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="btn-secondary"
              >
                {isEditing ? 'Abbrechen' : 'Schließen'}
              </button>
              {isEditing && (
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="btn-primary flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Speichern
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpertProfileModal;
